import './instrument';
import React from 'react';
import ReactDOM from 'react-dom/client';
import { reactErrorHandler } from '@sentry/react';
import './index.css';
// Loaded globally so ws-* styled pages outside the event workspace (Earnings,
// Vendors, Collaborators, …) don't depend on an unrelated lazy chunk having
// injected this stylesheet first.
import './styles/workspace.css';
import App from './App';
import { startAmplitude, stopAmplitude } from './utils/analytics';
import { hasAnalyticsConsent, subscribeConsent } from './utils/cookieConsent';

// Amplitude is a non-essential analytics tracker, so it only initialises once
// the visitor opts in to analytics cookies (see utils/cookieConsent.ts). Its
// autocapture also installs DOM listeners across the tree on init (~200-400ms
// of TBT during the LCP window), so even with consent we defer the actual init
// to idle so it doesn't compete with first paint.
const idleStartAmplitude = (): void => {
    type IdleWindow = Window & {
        requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
    };
    const idleWindow = window as IdleWindow;
    if (typeof idleWindow.requestIdleCallback === 'function') {
        idleWindow.requestIdleCallback(() => startAmplitude(), { timeout: 4000 });
    } else {
        setTimeout(() => startAmplitude(), 2000);
    }
};

// Skip in development — the SDK retries failed requests, which floods the
// console with noise when an ad-blocker (or DNS filter) blocks api2.amplitude.com.
if (process.env.NODE_ENV !== 'development') {
    if (hasAnalyticsConsent()) {
        idleStartAmplitude();
    }
    // React to a later decision: opt-in starts the SDK (idempotent), opt-out
    // halts sending for the rest of the session.
    subscribeConsent((consent) => {
        if (consent?.analytics) {
            idleStartAmplitude();
        } else {
            stopAmplitude();
        }
    });
}

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement,
  {
    onCaughtError: reactErrorHandler((error, errorInfo) => {
      console.warn('Caught error', error, errorInfo.componentStack);
    }),
    onUncaughtError: reactErrorHandler((error, errorInfo) => {
      console.error('Uncaught error', error, errorInfo.componentStack);
    }),
    onRecoverableError: reactErrorHandler(),
  }
);
root.render(
  <React.StrictMode>
    <App />
  </React.StrictMode>
);
